/**
 * /pay/success — Stripe redirects here after completing checkout.
 *
 * Polls GET /api/public/pay/success?session=<id> until the Payment row
 * reflects the actual status. Shows the appropriate state — never shows
 * a false "Payment received" when payment is still pending or failed.
 */

import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { BrandHeader } from '@/components/payment/BrandHeader';
import { usePaymentSession } from '@/hooks/usePaymentSession';

interface SuccessSearch {
  session?: string;
}

export const Route = createFileRoute('/pay/success')({
  validateSearch: (search: Record<string, unknown>): SuccessSearch => ({
    session: typeof search.session === 'string' ? search.session : undefined,
  }),
  component: PaySuccessPage,
});

function formatAmount(decimalStr: string): string {
  const n = parseFloat(decimalStr);
  if (!isFinite(n)) return decimalStr;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-start px-4 py-10">
      <div className="w-full max-w-lg">
        <BrandHeader />
        <main>
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-6 py-8 text-center"
            role="status"
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function PaySuccessPage() {
  const { session } = Route.useSearch();
  const status = usePaymentSession(session);

  if (status.phase === 'loading') {
    return (
      <PageShell>
        <Loader2 className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" aria-hidden="true" />
        <p className="text-gray-600 dark:text-gray-300">Checking your payment status…</p>
      </PageShell>
    );
  }

  if (status.phase === 'succeeded') {
    return (
      <PageShell>
        <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Payment received.</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Thank you! Your payment of{' '}
          <span className="font-semibold text-gray-700 dark:text-gray-200">{formatAmount(status.data.amount)}</span>{' '}
          for invoice{' '}
          <span className="font-semibold text-gray-700 dark:text-gray-200">#{status.data.invoiceNumber}</span> has been
          confirmed.
        </p>
        <p className="text-sm text-gray-400 mt-4">You will receive a confirmation email shortly.</p>
      </PageShell>
    );
  }

  if (status.phase === 'failed') {
    return (
      <PageShell>
        <AlertCircle
          className="h-12 w-12 text-red-400 mx-auto mb-4"
          aria-hidden="true"
          role="img"
          aria-label="Payment failed"
        />
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Payment did not complete.</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          We were unable to confirm your payment. No charge has been made.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Please{' '}
          <a href="mailto:billing@lyokotech.com" className="text-primary-600 dark:text-primary-400 hover:underline">
            contact us
          </a>{' '}
          if you believe this is an error.
        </p>
      </PageShell>
    );
  }

  if (status.phase === 'processing') {
    return (
      <PageShell>
        <Loader2
          className="h-12 w-12 text-primary-500 mx-auto mb-4 animate-spin"
          aria-hidden="true"
        />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          We&apos;re confirming your payment…
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm">This can take a moment. Please wait.</p>
      </PageShell>
    );
  }

  if (status.phase === 'timed-out') {
    return (
      <PageShell>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Your payment is still processing.
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          It&apos;s taking longer than expected. You will receive a confirmation email once your
          payment is confirmed — you can safely close this page.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          Questions?{' '}
          <a href="mailto:billing@lyokotech.com" className="text-primary-600 dark:text-primary-400 hover:underline">
            Contact us
          </a>
          .
        </p>
      </PageShell>
    );
  }

  // no-session or error — neutral fallback, never shows success
  return (
    <PageShell>
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Payment status unavailable.</h1>
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        We couldn&apos;t confirm your payment status. If you completed checkout, you will receive a
        confirmation email. Please{' '}
        <a href="mailto:billing@lyokotech.com" className="text-primary-600 dark:text-primary-400 hover:underline">
          contact us
        </a>{' '}
        if you have questions.
      </p>
    </PageShell>
  );
}
